import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../config";

const ADMIN_PASSWORD = "kathakala2026";

const TZ = {
  CST: { key: "CST", label: "CST", iana: "America/Chicago" },
  IST: { key: "IST", label: "IST", iana: "Asia/Kolkata" },
};

const FIELDS = [
  { key: "storyteller_telugu", label: "Telugu Name" },
  { key: "storyteller_english", label: "English Name" },
  { key: "writer_story_title", label: "Writer / Story" },
  { key: "introduction", label: "Introduction", multiline: true },
  { key: "story_length_mins", label: "Story Length (mins)" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "Email" },
];

const Manage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [activeTab, setActiveTab] = useState("registrations"); // "registrations" | "sessions" | "storytellers"

  const [slots, setSlots] = useState([]);
  const [storytellers, setStorytellers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [dateFilter, setDateFilter] = useState("all");
  const [tzKey, setTzKey] = useState("CST");
  const activeTZ = TZ[tzKey];

  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showListView, setShowListView] = useState(false);
  const [showWithoutPhotos, setShowWithoutPhotos] = useState(false);
  const [showWithoutStoryLength, setShowWithoutStoryLength] = useState(false);
  const [showLongStories, setShowLongStories] = useState(false);

  const timeFormatter = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: activeTZ.iana,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, [activeTZ.iana]);

  const dateFormatter = useMemo(() => {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: activeTZ.iana,
      month: "short",
      day: "numeric",
      weekday: "short",
    });
  }, [activeTZ.iana]);

  const displaySlots = useMemo(() => {
    let filtered = slots;

    if (showDuplicates) {
      const nameMap = {};
      filtered.forEach((slot) => {
        const teller = storytellers[slot.slot_id];
        const name = teller?.storyteller_telugu?.trim();
        if (!name) return;
        if (!nameMap[name]) nameMap[name] = [];
        nameMap[name].push(slot.slot_id);
      });

      const dupSlotIds = new Set();
      Object.values(nameMap).forEach((ids) => {
        if (ids.length >= 2) ids.forEach((id) => dupSlotIds.add(id));
      });

      filtered = filtered.filter((slot) => dupSlotIds.has(slot.slot_id));
    }

    if (showPhotos) {
      filtered = filtered.filter((slot) => storytellers[slot.slot_id]?.photo_url);
    }

    if (showWithoutPhotos) {
      filtered = filtered.filter((slot) => !storytellers[slot.slot_id]?.photo_url);
    }

    if (showWithoutStoryLength) {
      filtered = filtered.filter((slot) => !storytellers[slot.slot_id]?.story_length_mins);
    }

    if (showLongStories) {
      filtered = filtered.filter((slot) => storytellers[slot.slot_id]?.story_length_mins > 9);
    }

    return filtered;
  }, [showDuplicates, showPhotos, showWithoutPhotos, showWithoutStoryLength, showLongStories, slots, storytellers]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password.");
    }
  };

  const anyFilterActive = showDuplicates || showPhotos || showWithoutPhotos || showWithoutStoryLength || showLongStories;

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMsg("");
    try {
      let slotQuery = supabase
        .from("slots")
        .select("slot_id, slot_date, slot_from, slot_to, is_booked")
        .eq("is_booked", true)
        .order("slot_from", { ascending: true });

      if (dateFilter !== "all" && !anyFilterActive) {
        slotQuery = slotQuery.eq("slot_date", dateFilter);
      }

      const { data: slotData, error: slotError } = await slotQuery;
      if (slotError) throw slotError;

      const list = slotData || [];
      setSlots(list);

      const slotIds = list.map((s) => s.slot_id);
      if (slotIds.length > 0) {
        const { data: tellerData, error: tellerError } = await supabase
          .from("storytellers")
          .select("slot_id, storyteller_telugu, storyteller_english, writer_story_title, introduction, whatsapp, email, photo_url, video_url, story_length_mins")
          .in("slot_id", slotIds);

        if (tellerError) throw tellerError;

        const map = {};
        (tellerData || []).forEach((t) => {
          map[t.slot_id] = t;
        });
        setStorytellers(map);
      } else {
        setStorytellers({});
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setErrorMsg(err?.message || "Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dateFilter, showDuplicates, showPhotos, showWithoutPhotos, showWithoutStoryLength, showLongStories]);

  const formatTime = (isoString) => {
    try {
      return timeFormatter.format(new Date(isoString));
    } catch {
      return "—";
    }
  };

  const formatDate = (isoString) => {
    try {
      return dateFormatter.format(new Date(isoString));
    } catch {
      return "—";
    }
  };

  const startEdit = (slotId) => {
    const teller = storytellers[slotId];
    if (!teller) return;
    setEditingSlotId(slotId);
    setEditData({
      storyteller_telugu: teller.storyteller_telugu || "",
      storyteller_english: teller.storyteller_english || "",
      writer_story_title: teller.writer_story_title || "",
      introduction: teller.introduction || "",
      whatsapp: teller.whatsapp || "",
      email: teller.email || "",
      photo_url: teller.photo_url || "",
      video_url: teller.video_url || "",
      story_length_mins: teller.story_length_mins != null ? String(teller.story_length_mins) : "",
    });
  };

  const cancelEdit = () => {
    setEditingSlotId(null);
    setEditData({});
  };

  const saveEdit = async (slotId) => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("storytellers")
        .update({
          storyteller_telugu: editData.storyteller_telugu,
          storyteller_english: editData.storyteller_english,
          writer_story_title: editData.writer_story_title,
          introduction: editData.introduction,
          whatsapp: editData.whatsapp,
          email: editData.email,
          photo_url: editData.photo_url || null,
          video_url: editData.video_url || null,
          story_length_mins: editData.story_length_mins ? parseFloat(editData.story_length_mins) : null,
        })
        .eq("slot_id", slotId);

      if (error) throw error;

      setEditingSlotId(null);
      setEditData({});
      await fetchData();
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save: " + (err?.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  const releaseSlot = async (slotId) => {
    if (!window.confirm("Are you sure you want to release this slot? The storyteller record will be deleted.")) {
      return;
    }
    try {
      // Delete storyteller_slides first (foreign key dependency)
      const { error: slidesError } = await supabase
        .from("storyteller_slides")
        .delete()
        .eq("slot_id", slotId);

      if (slidesError) throw slidesError;

      // Delete the storyteller record
      const { error: delError } = await supabase
        .from("storytellers")
        .delete()
        .eq("slot_id", slotId);

      if (delError) throw delError;

      // Then mark the slot as unbooked
      const { error } = await supabase
        .from("slots")
        .update({ is_booked: false })
        .eq("slot_id", slotId);

      if (error) throw error;
      await fetchData();
    } catch (err) {
      console.error("Error releasing slot:", err);
      alert("Failed to release slot: " + (err?.message || "Unknown error"));
    }
  };

  const downloadCSV = () => {
    const headers = ["Slot ID", "Date", "Time From", "Time To", "Telugu Name", "English Name", "Writer / Story", "Introduction", "Story Length (mins)", "WhatsApp", "Email", "Photo URL", "Video URL"];
    const rows = displaySlots.map((slot) => {
      const teller = storytellers[slot.slot_id] || {};
      return [
        slot.slot_id,
        formatDate(slot.slot_from),
        formatTime(slot.slot_from),
        formatTime(slot.slot_to),
        teller.storyteller_telugu || "",
        teller.storyteller_english || "",
        teller.writer_story_title || "",
        (teller.introduction || "").replace(/\n/g, " "),
        teller.story_length_mins != null ? teller.story_length_mins : "",
        teller.whatsapp || "",
        teller.email || "",
        teller.photo_url || "",
        teller.video_url || "",
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `kathakala_registrations_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getFilterLabel = () => {
    if (showDuplicates) return `${displaySlots.length} duplicate registration${displaySlots.length !== 1 ? "s" : ""}`;
    if (showPhotos) return `${displaySlots.length} with photo${displaySlots.length !== 1 ? "s" : ""}`;
    if (showWithoutPhotos) return `${displaySlots.length} without photo${displaySlots.length !== 1 ? "s" : ""}`;
    if (showWithoutStoryLength) return `${displaySlots.length} without story length`;
    if (showLongStories) return `${displaySlots.length} with story length > 9 min`;
    return `${slots.length} booked slot${slots.length !== 1 ? "s" : ""}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="bg-white p-8 rounded-lg shadow-md w-full max-w-sm"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
            Admin Access
          </h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            className="w-full p-3 border rounded-md mb-3 focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          {passwordError && (
            <p className="text-red-600 text-sm mb-3">{passwordError}</p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 rounded-md hover:bg-blue-700 font-semibold"
          >
            Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          Admin Dashboard
        </h1>

        {/* Quick Links to other admin sections */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <a href="/judge" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs font-semibold hover:bg-amber-100 transition">
            ⚖️ Judge Scoring
          </a>
          <a href="/puraskaralu-admin" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-purple-700 text-xs font-semibold hover:bg-purple-100 transition">
            🏆 Puraskaralu Admin
          </a>
          <a href="/episode-creator" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-xs font-semibold hover:bg-blue-100 transition">
            🎬 Episode Creator
          </a>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 max-w-lg mx-auto">
          {[
            { key: "registrations", label: "Registrations" },
            { key: "sessions", label: "Sessions" },
            { key: "storytellers", label: "Storytellers" },
            { key: "performances", label: "Performances" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "sessions" && (
          <SessionsTab supabase={supabase} />
        )}

        {activeTab === "storytellers" && (
          <StorytellersTab supabase={supabase} />
        )}

        {activeTab === "performances" && (
          <PerformancesTab supabase={supabase} />
        )}

        {activeTab !== "registrations" ? null : (
        <>
        {/* Controls */}
        <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <select
              className="p-2 border rounded-md focus:ring-2 focus:ring-blue-500 bg-white"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="2026-03-21">21 March 2026</option>
              <option value="2026-03-22">22 March 2026</option>
            </select>

            <div className="flex items-center gap-1 border rounded-md p-1 bg-white">
              <button
                onClick={() => setTzKey("CST")}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                  tzKey === "CST"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                type="button"
              >
                CST
              </button>
              <button
                onClick={() => setTzKey("IST")}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                  tzKey === "IST"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                type="button"
              >
                IST
              </button>
            </div>

            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 text-gray-700"
              disabled={isLoading}
              type="button"
            >
              {isLoading ? "Loading..." : "Refresh"}
            </button>

            <button
              onClick={() => setShowDuplicates((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showDuplicates
                  ? "bg-orange-500 text-white border-orange-500"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showDuplicates ? "Showing Duplicates" : "Show Duplicates"}
            </button>

            <button
              onClick={() => setShowPhotos((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showPhotos
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showPhotos ? "Showing Photos" : "Show Photos"}
            </button>

            <button
              onClick={() => setShowWithoutPhotos((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showWithoutPhotos
                  ? "bg-pink-600 text-white border-pink-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showWithoutPhotos ? "Without Photos" : "No Photo"}
            </button>

            <button
              onClick={() => setShowWithoutStoryLength((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showWithoutStoryLength
                  ? "bg-amber-600 text-white border-amber-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showWithoutStoryLength ? "No Story Length" : "No Length"}
            </button>

            <button
              onClick={() => setShowLongStories((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showLongStories
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showLongStories ? "> 9 min" : "Long (>9 min)"}
            </button>

            <button
              onClick={() => setShowListView((prev) => !prev)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                showListView
                  ? "bg-teal-600 text-white border-teal-600"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
              type="button"
            >
              {showListView ? "Card View" : "List View"}
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              {getFilterLabel()}
            </span>
            {displaySlots.length > 0 && (
              <button
                onClick={downloadCSV}
                className="px-3 py-1.5 rounded-md border text-sm font-semibold bg-white text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Download CSV
              </button>
            )}
          </div>
        </div>

        {errorMsg && (
          <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {errorMsg}
          </div>
        )}

        {isLoading ? (
          <div className="py-10 text-center text-gray-600">Loading...</div>
        ) : displaySlots.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            {anyFilterActive ? "No matching entries found." : "No booked slots found."}
          </div>
        ) : showListView ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse bg-white rounded-lg shadow">
              <thead>
                <tr className="bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">
                  <th className="p-3 border-b">#</th>
                  <th className="p-3 border-b">ID</th>
                  <th className="p-3 border-b">Date / Time</th>
                  <th className="p-3 border-b">Photo</th>
                  <th className="p-3 border-b">Video</th>
                  <th className="p-3 border-b">Telugu Name</th>
                  <th className="p-3 border-b">English Name</th>
                  <th className="p-3 border-b">Writer / Story</th>
                  <th className="p-3 border-b">Length</th>
                  <th className="p-3 border-b">Introduction</th>
                  <th className="p-3 border-b">WhatsApp</th>
                  <th className="p-3 border-b">Email</th>
                </tr>
              </thead>
              <tbody>
                {displaySlots.map((slot, index) => {
                  const teller = storytellers[slot.slot_id] || {};
                  return (
                    <tr key={slot.slot_id} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-gray-500">{index + 1}</td>
                      <td className="p-3 font-mono text-gray-600">{slot.slot_id}</td>
                      <td className="p-3 whitespace-nowrap">
                        {formatDate(slot.slot_from)}<br />
                        <span className="text-gray-500">{formatTime(slot.slot_from)} – {formatTime(slot.slot_to)} {activeTZ.label}</span>
                      </td>
                      <td className="p-3">
                        {teller.photo_url ? (
                          <img src={teller.photo_url} alt="" className="w-10 h-10 object-cover rounded border" />
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        {teller.video_url ? (
                          <a href={teller.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">View</a>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="p-3">{teller.storyteller_telugu || "—"}</td>
                      <td className="p-3">{teller.storyteller_english || "—"}</td>
                      <td className="p-3">{teller.writer_story_title || "—"}</td>
                      <td className="p-3">{teller.story_length_mins != null ? teller.story_length_mins : "—"}</td>
                      <td className="p-3 max-w-xs truncate" title={teller.introduction}>{teller.introduction || "—"}</td>
                      <td className="p-3 whitespace-nowrap">{teller.whatsapp || "—"}</td>
                      <td className="p-3">{teller.email || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          ) : (
          <div className="space-y-4">
            {displaySlots.map((slot, index) => {
              const teller = storytellers[slot.slot_id] || {};
              const isEditing = editingSlotId === slot.slot_id;

              return (
                <div
                  key={slot.slot_id}
                  className={`bg-white rounded-lg shadow p-4 border-l-4 ${
                    isEditing ? "border-yellow-500" : "border-blue-500"
                  }`}
                >
                  {/* Card Header: Slot # + Time + Actions */}
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                      <span className="text-xs font-semibold text-gray-400 uppercase">
                        Slot {index + 1}
                      </span>
                      <div className="text-sm font-semibold text-gray-800">
                        {formatDate(slot.slot_from)} &middot;{" "}
                        {formatTime(slot.slot_from)} – {formatTime(slot.slot_to)}{" "}
                        <span className="text-gray-500">{activeTZ.label}</span>
                      </div>
                    </div>

                    {!isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEdit(slot.slot_id)}
                          className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => releaseSlot(slot.slot_id)}
                          className="px-4 py-1.5 bg-red-600 text-white rounded text-sm font-semibold hover:bg-red-700"
                        >
                          Release
                        </button>
                      </div>
                    )}
                    {isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => saveEdit(slot.slot_id)}
                          disabled={isSaving}
                          className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={isSaving}
                          className="px-4 py-1.5 border rounded text-sm font-semibold hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Photo Thumbnail */}
                  {isEditing ? (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-500 mb-0.5">Photo URL</label>
                      <div className="flex items-center gap-3">
                        <input
                          className="flex-1 p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          value={editData.photo_url}
                          onChange={(e) => setEditData({ ...editData, photo_url: e.target.value })}
                          placeholder="Google Drive thumbnail URL"
                        />
                        {editData.photo_url && (
                          <img
                            src={editData.photo_url}
                            alt="Preview"
                            className="w-16 h-16 object-cover rounded border border-gray-300"
                          />
                        )}
                      </div>
                    </div>
                  ) : teller.photo_url ? (
                    <div className="mb-3">
                      <img
                        src={teller.photo_url}
                        alt="Storyteller"
                        className="w-16 h-16 object-cover rounded border border-gray-300"
                      />
                    </div>
                  ) : null}

                  {/* Video URL */}
                  {isEditing ? (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-500 mb-0.5">Video URL</label>
                      <div className="flex items-center gap-3">
                        <input
                          className="flex-1 p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                          value={editData.video_url}
                          onChange={(e) => setEditData({ ...editData, video_url: e.target.value })}
                          placeholder="Google Drive video URL"
                        />
                        {editData.video_url && (
                          <a href={editData.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm whitespace-nowrap">Preview</a>
                        )}
                      </div>
                    </div>
                  ) : teller.video_url ? (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-gray-500 mb-0.5">Video</label>
                      <a href={teller.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-sm">View Video</a>
                    </div>
                  ) : null}

                  {/* Card Body: Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {FIELDS.map((field) => (
                      <div
                        key={field.key}
                        className={field.multiline ? "md:col-span-2" : ""}
                      >
                        <label className="block text-xs font-semibold text-gray-500 mb-0.5">
                          {field.label}
                        </label>
                        {isEditing ? (
                          field.multiline ? (
                            <textarea
                              className="w-full p-2 border rounded text-sm resize-none h-20 focus:ring-2 focus:ring-blue-500"
                              value={editData[field.key]}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  [field.key]: e.target.value,
                                })
                              }
                            />
                          ) : (
                            <input
                              className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                              value={editData[field.key]}
                              onChange={(e) =>
                                setEditData({
                                  ...editData,
                                  [field.key]: e.target.value,
                                })
                              }
                            />
                          )
                        ) : (
                          <p className="text-sm text-gray-800 break-words">
                            {teller[field.key] != null ? String(teller[field.key]) : "—"}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   Sessions Management Tab
   ═══════════════════════════════════════════════════════ */
const SessionsTab = ({ supabase }) => {
  const [sessions, setSessions] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [peopleLookup, setPeopleLookup] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const cstFmt = useMemo(() => new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hour12: true }), []);
  const istFmt = useMemo(() => new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true }), []);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const { data: sessData } = await supabase.from("sessions").select("*").order("session_id");
      setSessions(sessData || []);

      // Fetch all host-eligible and analyst-eligible storytellers
      const { data: hostData } = await supabase.from("storytellers").select("storyteller_id, storyteller_telugu, storyteller_english, photo_url").eq("is_host", true);
      const { data: analystData } = await supabase.from("storytellers").select("storyteller_id, storyteller_telugu, storyteller_english, photo_url").eq("is_story_analyst", true);
      setHosts(hostData || []);
      setAnalysts(analystData || []);

      const lookup = {};
      [...(hostData || []), ...(analystData || [])].forEach((p) => { lookup[p.storyteller_id] = p; });
      setPeopleLookup(lookup);
    } catch (err) {
      console.error("Error fetching sessions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (session) => {
    setEditingId(session.session_id);
    setEditData({
      session_title: session.session_title || "",
      session_honor_title_telugu: session.session_honor_title_telugu || "",
      session_honor_title_english: session.session_honor_title_english || "",
      host_id: session.host_id || "",
      analyst_id: session.analyst_id || "",
      youtube_live_url: session.youtube_live_url || "",
      facebook_live_url: session.facebook_live_url || "",
      poster_image_url: session.poster_image_url || "",
    });
  };

  const saveEdit = async (sessionId) => {
    setIsSaving(true);
    try {
      const { error } = await supabase.from("sessions").update({
        session_title: editData.session_title,
        session_honor_title_telugu: editData.session_honor_title_telugu,
        session_honor_title_english: editData.session_honor_title_english,
        host_id: editData.host_id || null,
        analyst_id: editData.analyst_id || null,
        youtube_live_url: editData.youtube_live_url || null,
        facebook_live_url: editData.facebook_live_url || null,
        poster_image_url: editData.poster_image_url || null,
      }).eq("session_id", sessionId);
      if (error) throw error;
      setEditingId(null);
      await fetchAll();
    } catch (err) {
      alert("Failed to save: " + (err?.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="py-10 text-center text-gray-600">Loading sessions...</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 text-center mb-2">{sessions.length} sessions</p>
      {sessions.map((session) => {
        const isEditing = editingId === session.session_id;
        const hostPerson = peopleLookup[session.host_id];
        const analystPerson = peopleLookup[session.analyst_id];
        const cstFrom = cstFmt.format(new Date(session.session_from));
        const cstTo = cstFmt.format(new Date(session.session_to));
        const istFrom = istFmt.format(new Date(session.session_from));
        const istTo = istFmt.format(new Date(session.session_to));

        return (
          <div key={session.session_id} className={`bg-white rounded-lg shadow p-4 border-l-4 ${isEditing ? "border-yellow-500" : session.day === 1 ? "border-orange-400" : "border-blue-400"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div>
                <span className="text-xs font-semibold text-gray-400">Session {session.session_id} — Day {session.day}</span>
                <div className="text-xs text-gray-500">
                  🇺🇸 {cstFrom} – {cstTo} CST &nbsp; 🇮🇳 {istFrom} – {istTo} IST
                </div>
              </div>
              {!isEditing ? (
                <button onClick={() => startEdit(session)} className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-semibold hover:bg-blue-700">Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(session.session_id)} disabled={isSaving} className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 disabled:opacity-50">{isSaving ? "Saving..." : "Save"}</button>
                  <button onClick={() => setEditingId(null)} className="px-4 py-1.5 border rounded text-sm font-semibold hover:bg-gray-50">Cancel</button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Session Title (URL slug)</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.session_title} onChange={(e) => setEditData({ ...editData, session_title: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Honor Title (Telugu)</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.session_honor_title_telugu} onChange={(e) => setEditData({ ...editData, session_honor_title_telugu: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Honor Title (English)</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.session_honor_title_english} onChange={(e) => setEditData({ ...editData, session_honor_title_english: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Host</label>
                  <select className="w-full p-2 border rounded text-sm" value={editData.host_id} onChange={(e) => setEditData({ ...editData, host_id: e.target.value })}>
                    <option value="">— Select Host —</option>
                    {hosts.map((h) => <option key={h.storyteller_id} value={h.storyteller_id}>{h.storyteller_telugu} — {h.storyteller_english}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Story Analyst</label>
                  <select className="w-full p-2 border rounded text-sm" value={editData.analyst_id} onChange={(e) => setEditData({ ...editData, analyst_id: e.target.value })}>
                    <option value="">— Select Analyst —</option>
                    {analysts.map((a) => <option key={a.storyteller_id} value={a.storyteller_id}>{a.storyteller_telugu} — {a.storyteller_english}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">YouTube Live URL</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.youtube_live_url} onChange={(e) => setEditData({ ...editData, youtube_live_url: e.target.value })} placeholder="https://youtube.com/live/..." />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Facebook Live URL</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.facebook_live_url} onChange={(e) => setEditData({ ...editData, facebook_live_url: e.target.value })} placeholder="https://facebook.com/..." />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 mb-0.5">Poster Image URL</label>
                  <input className="w-full p-2 border rounded text-sm" value={editData.poster_image_url} onChange={(e) => setEditData({ ...editData, poster_image_url: e.target.value })} placeholder="https://..." />
                </div>
              </div>
            ) : (
              <div>
                <div className="text-lg font-bold text-gray-800">{session.session_honor_title_telugu}</div>
                <div className="text-sm text-gray-600 mb-2">{session.session_honor_title_english}</div>
                <div className="flex flex-wrap gap-3 text-xs">
                  {hostPerson && (
                    <span className="bg-green-50 text-green-800 px-2 py-1 rounded-md">🎤 Host: {hostPerson.storyteller_english}</span>
                  )}
                  {analystPerson && (
                    <span className="bg-purple-50 text-purple-800 px-2 py-1 rounded-md">📝 Analyst: {analystPerson.storyteller_english}</span>
                  )}
                  {session.youtube_live_url && (
                    <a href={session.youtube_live_url} target="_blank" rel="noopener noreferrer" className="bg-red-50 text-red-700 px-2 py-1 rounded-md hover:underline">▶ YouTube</a>
                  )}
                  {session.facebook_live_url && (
                    <a href={session.facebook_live_url} target="_blank" rel="noopener noreferrer" className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md hover:underline">📘 Facebook</a>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-1">slug: /{session.session_title}</div>

                {/* Poster preview & redo actions */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-3">
                  {session.poster_image_url && (
                    <img
                      src={session.poster_image_url}
                      alt={`Vedika ${session.session_id} poster`}
                      className="w-40 h-auto rounded shadow-sm border"
                    />
                  )}
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => {
                        const cmd = `node scripts/generate-session-posters.js --session ${session.session_id} --reset && node scripts/export-session-posters.js --session ${session.session_id}`;
                        navigator.clipboard.writeText(cmd);
                        alert("Copied to clipboard:\\n\\n" + cmd);
                      }}
                      className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-semibold hover:bg-amber-700"
                    >
                      Redo Poster
                    </button>
                    <a
                      href={`https://docs.google.com/presentation/d/1JwRaTmZq5g8Wm7ZBFbUvT-VPGxDhAuAK9fAaAJr7DfA/edit#slide=id.${session.session_id === 1 ? "p" : ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold hover:bg-gray-200 text-center"
                    >
                      Open Slide Deck
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   Performances Tab — Browse & Review AI Ratings
   ═══════════════════════════════════════════════════════ */
const PerformancesTab = ({ supabase }) => {
  const [performances, setPerformances] = useState([]);
  const [episodes, setEpisodes] = useState({});
  const [stories, setStories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ratingFilter, setRatingFilter] = useState("all"); // all | rated | unrated
  const [sortBy, setSortBy] = useState("updated_desc"); // updated_desc | date_desc | date_asc | rating_desc
  const [expandedId, setExpandedId] = useState(null);
  const [editingPerf, setEditingPerf] = useState(null); // performance_id being edited
  const [editFields, setEditFields] = useState({});
  const [isSavingPerf, setIsSavingPerf] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [perfRes, epRes, storyRes] = await Promise.all([
        supabase.from("performances").select("*").order("updated_at", { ascending: false, nullsFirst: false }).limit(2000),
        supabase.from("episodes").select("episode_id, video_id, publish_date, host_name_telugu"),
        supabase.from("stories").select("story_id, title_telugu, writer_id, writers(name_telugu)"),
      ]);
      setPerformances(perfRes.data || []);
      const epMap = {};
      (epRes.data || []).forEach(e => { epMap[e.episode_id] = e; });
      setEpisodes(epMap);
      const stMap = {};
      (storyRes.data || []).forEach(s => { stMap[s.story_id] = s; });
      setStories(stMap);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = performances;
    if (ratingFilter === "rated") list = list.filter(p => p.rating_story !== null);
    else if (ratingFilter === "unrated") list = list.filter(p => p.rating_story === null);
    else if (ratingFilter === "granular") list = list.filter(p => p.shruti !== null);
    else if (ratingFilter === "no_granular") list = list.filter(p => p.rating_story !== null && p.shruti === null);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(p =>
        (p.storyteller_name_telugu || "").toLowerCase().includes(q) ||
        (p.storyteller_name_english || "").toLowerCase().includes(q) ||
        (stories[p.story_id]?.title_telugu || "").toLowerCase().includes(q) ||
        (episodes[p.episode_id]?.video_id || "").includes(q)
      );
    }
    // Sort
    list = [...list].sort((a, b) => {
      if (sortBy === "updated_desc") {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return b.updated_at.localeCompare(a.updated_at);
      }
      if (sortBy === "rating_desc") {
        return (b.rating_story || 0) - (a.rating_story || 0);
      }
      if (sortBy === "cat_asc" || sortBy === "cat_desc") {
        const ca = (a.category || "zzz"), cb = (b.category || "zzz");
        return sortBy === "cat_asc" ? ca.localeCompare(cb) : cb.localeCompare(ca);
      }
      if (sortBy === "name_asc" || sortBy === "name_desc") {
        const na = (a.storyteller_name_telugu || ""), nb = (b.storyteller_name_telugu || "");
        return sortBy === "name_asc" ? na.localeCompare(nb) : nb.localeCompare(na);
      }
      const dateA = episodes[a.episode_id]?.publish_date || "";
      const dateB = episodes[b.episode_id]?.publish_date || "";
      return sortBy === "date_asc" ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
    return list;
  }, [performances, ratingFilter, search, stories, episodes, sortBy]);

  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const ratedCount = performances.filter(p => p.rating_story !== null).length;
  const granularCount = performances.filter(p => p.shruti !== null).length;

  // Helper: parse "H:MM:SS" or "HH:MM:SS" to seconds
  const timeToSec = (t) => {
    if (!t) return null;
    const parts = t.split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return null;
  };

  const startEditPerf = (p) => {
    setEditingPerf(p.performance_id);
    setEditFields({
      storyteller_name_telugu: p.storyteller_name_telugu || "",
      storyteller_name_english: p.storyteller_name_english || "",
      title: p.title || "",
      is_author: !!p.is_author,
      category: p.category || "",
      story_start_time: p.story_start_time || "",
    });
    setSaveMsg("");
  };

  const cancelEditPerf = () => {
    setEditingPerf(null);
    setEditFields({});
    setSaveMsg("");
  };

  const savePerf = async (perfId) => {
    setIsSavingPerf(true);
    setSaveMsg("");
    try {
      const startSec = timeToSec(editFields.story_start_time);
      const updates = {
        storyteller_name_telugu: editFields.storyteller_name_telugu || null,
        storyteller_name_english: editFields.storyteller_name_english || null,
        title: editFields.title || null,
        is_author: editFields.is_author,
        category: editFields.category || null,
        story_start_time: editFields.story_start_time || null,
        story_start_sec: startSec,
      };
      const { error } = await supabase.from("performances").update(updates).eq("performance_id", perfId);
      if (error) throw error;
      // Update local state
      setPerformances(prev => prev.map(p => p.performance_id === perfId ? { ...p, ...updates } : p));
      setSaveMsg("Saved!");
      setTimeout(() => setSaveMsg(""), 2000);
      setEditingPerf(null);
    } catch (err) {
      setSaveMsg("Error: " + err.message);
    } finally {
      setIsSavingPerf(false);
    }
  };

  const bulkRenameStoryteller = async (oldName, newName, newTitle) => {
    if (!oldName || !newName) return;
    const toUpdate = performances.filter(p => p.storyteller_name_telugu === oldName);
    if (toUpdate.length === 0) return;
    if (!window.confirm(`Rename "${oldName}" → "${newName}" across ${toUpdate.length} performances?`)) return;
    setIsSavingPerf(true);
    setSaveMsg("");
    try {
      const updates = { storyteller_name_telugu: newName };
      if (newTitle !== undefined) updates.title = newTitle || null;
      const { error } = await supabase.from("performances").update(updates).eq("storyteller_name_telugu", oldName);
      if (error) throw error;
      setPerformances(prev => prev.map(p => p.storyteller_name_telugu === oldName ? { ...p, ...updates } : p));
      setSaveMsg(`Renamed ${toUpdate.length} entries!`);
      setTimeout(() => setSaveMsg(""), 3000);
    } catch (err) {
      setSaveMsg("Error: " + err.message);
    } finally {
      setIsSavingPerf(false);
    }
  };

  if (isLoading) return <div className="py-10 text-center text-gray-600">Loading performances...</div>;

  return (
    <div>
      {/* Stats bar */}
      <div className="bg-white rounded-lg shadow-sm p-3 mb-4 flex flex-wrap gap-4 items-center text-sm">
        <span className="font-semibold text-gray-700">Total: {performances.length}</span>
        <span className="text-green-700">Rated: {ratedCount}</span>
        <span className="text-blue-700">Granular: {granularCount}</span>
        <span className="text-red-600">Unrated: {performances.length - ratedCount}</span>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <input
          type="text"
          placeholder="Search storyteller, story, video ID..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="px-3 py-2 border rounded-md text-sm w-64 focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex gap-1">
          {[
            { key: "all", label: "All" },
            { key: "rated", label: "Rated" },
            { key: "unrated", label: "Unrated" },
            { key: "granular", label: "Has Granular" },
            { key: "no_granular", label: "No Granular" },
          ].map(f => (
            <button key={f.key} onClick={() => { setRatingFilter(f.key); setPage(0); }}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                ratingFilter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}>{f.label}</button>
          ))}
        </div>
        <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }}
          className="px-2 py-1.5 border rounded-md text-xs font-semibold">
          <option value="updated_desc">Recently updated</option>
          <option value="date_desc">Latest episode</option>
          <option value="date_asc">Oldest episode</option>
          <option value="rating_desc">Highest rated</option>
          <option value="cat_asc">Category A→Z</option>
          <option value="cat_desc">Category Z→A</option>
          <option value="name_asc">Name A→Z</option>
          <option value="name_desc">Name Z→A</option>
        </select>
        <span className="text-xs text-gray-500">{filtered.length} results</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200" onClick={() => setSortBy(sortBy === "name_asc" ? "name_desc" : "name_asc")}>కథకులు {sortBy === "name_asc" ? "▲" : sortBy === "name_desc" ? "▼" : ""}</th>
                <th className="px-2 py-2 text-left">కథ</th>
                <th className="px-2 py-2 text-left">రచయిత</th>
                <th className="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" onClick={() => setSortBy(sortBy === "date_asc" ? "date_desc" : "date_asc")}>Date {sortBy === "date_asc" ? "▲" : sortBy === "date_desc" ? "▼" : ""}</th>
                <th className="px-2 py-2 text-center cursor-pointer hover:bg-gray-200" onClick={() => setSortBy(sortBy === "cat_asc" ? "cat_desc" : "cat_asc")}>Category {sortBy === "cat_asc" ? "▲" : sortBy === "cat_desc" ? "▼" : ""}</th>
                <th className="px-2 py-2 text-center bg-amber-50 cursor-pointer hover:bg-amber-100" onClick={() => setSortBy("rating_desc")}>S {sortBy === "rating_desc" ? "▼" : ""}</th>
                <th className="px-2 py-2 text-center bg-amber-50">ST</th>
                <th className="px-2 py-2 text-center bg-amber-50">W</th>
                <th className="px-2 py-2 text-center bg-blue-50">Shr</th>
                <th className="px-2 py-2 text-center bg-blue-50">Mod</th>
                <th className="px-2 py-2 text-center bg-blue-50">Uch</th>
                <th className="px-2 py-2 text-center bg-blue-50">Gat</th>
                <th className="px-2 py-2 text-center bg-green-50">Rd</th>
                <th className="px-2 py-2 text-center bg-green-50">Va</th>
                <th className="px-2 py-2 text-center bg-green-50">Sh</th>
                <th className="px-2 py-2 text-center bg-green-50">In</th>
                <th className="px-2 py-2 text-center">▶</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p, idx) => {
                const ep = episodes[p.episode_id];
                const story = stories[p.story_id];
                const writer = story?.writers?.name_telugu || "";
                const isExpanded = expandedId === p.performance_id;
                const videoUrl = ep?.video_id ? `https://www.youtube.com/watch?v=${ep.video_id}${p.story_start_sec ? `&t=${p.story_start_sec}s` : ""}` : null;
                const catShort = (p.category || "").replace("తొలి కథనం - ", "తొ.").replace("కలం-గళం", "క-గ").replace("గళకళ", "గళ");

                return (
                  <React.Fragment key={p.performance_id}>
                    <tr
                      className={`border-b hover:bg-gray-50 cursor-pointer ${isExpanded ? "bg-yellow-50" : ""} ${!p.rating_story ? "bg-red-50/30" : ""}`}
                      onClick={() => setExpandedId(isExpanded ? null : p.performance_id)}
                    >
                      <td className="px-2 py-1.5 text-gray-400">{page * PAGE_SIZE + idx + 1}</td>
                      <td className="px-2 py-1.5 font-medium text-gray-800 max-w-[160px] truncate" title={p.storyteller_name_telugu}>
                        {p.storyteller_name_telugu || p.storyteller_name_english || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-gray-700 max-w-[140px] truncate" title={story?.title_telugu}>
                        {story?.title_telugu || "—"}
                      </td>
                      <td className="px-2 py-1.5 text-gray-600 max-w-[120px] truncate" title={writer}>{writer || "—"}</td>
                      <td className="px-2 py-1.5 text-center text-gray-500">{ep?.publish_date || "—"}</td>
                      <td className="px-2 py-1.5 text-center">
                        {catShort && <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          p.category?.includes("గళకళ") ? "bg-amber-100 text-amber-800" :
                          p.category?.includes("కలం") ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"
                        }`}>{catShort}</span>}
                      </td>
                      <td className="px-2 py-1.5 text-center bg-amber-50/50">{p.rating_story ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-amber-50/50">{p.rating_storyteller ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-amber-50/50">{p.rating_writer ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-blue-50/50">{p.shruti ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-blue-50/50">{p.modulation ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-blue-50/50">{p.uccharana ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-blue-50/50">{p.gati ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-green-50/50">{p.readability ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-green-50/50">{p.vastu ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-green-50/50">{p.shilpam ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center bg-green-50/50">{p.innovation ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center">
                        {videoUrl && (
                          <a href={videoUrl} target="_blank" rel="noopener noreferrer"
                            className="text-red-600 hover:text-red-800" title="Watch on YouTube"
                            onClick={(e) => e.stopPropagation()}>▶</a>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-yellow-50 border-b">
                        <td colSpan={18} className="px-4 py-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Column 1: Editable fields */}
                            <div className="md:col-span-2">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="text-xs font-semibold text-gray-500">PID: {p.performance_id}</div>
                                {editingPerf !== p.performance_id ? (
                                  <button onClick={(e) => { e.stopPropagation(); startEditPerf(p); }}
                                    className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Edit</button>
                                ) : (
                                  <div className="flex gap-1">
                                    <button onClick={(e) => { e.stopPropagation(); savePerf(p.performance_id); }}
                                      disabled={isSavingPerf}
                                      className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                                      {isSavingPerf ? "Saving..." : "Save"}
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); cancelEditPerf(); }}
                                      className="px-2 py-0.5 text-xs bg-gray-400 text-white rounded hover:bg-gray-500">Cancel</button>
                                    {saveMsg && <span className={`text-xs ${saveMsg.startsWith("Error") ? "text-red-600" : "text-green-600"}`}>{saveMsg}</span>}
                                  </div>
                                )}
                              </div>

                              {editingPerf === p.performance_id ? (
                                <div className="grid grid-cols-2 gap-2 text-sm" onClick={(e) => e.stopPropagation()}>
                                  <div>
                                    <label className="text-xs text-gray-500 block">కథకులు (Telugu)</label>
                                    <input value={editFields.storyteller_name_telugu}
                                      onChange={(e) => setEditFields({...editFields, storyteller_name_telugu: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500 block">Storyteller (English)</label>
                                    <input value={editFields.storyteller_name_english}
                                      onChange={(e) => setEditFields({...editFields, storyteller_name_english: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm" />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500 block">Title (డా., శ్రీ, etc.)</label>
                                    <input value={editFields.title}
                                      onChange={(e) => setEditFields({...editFields, title: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm" placeholder="డా." />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500 block">Is Author?</label>
                                    <select value={editFields.is_author ? "true" : "false"}
                                      onChange={(e) => setEditFields({...editFields, is_author: e.target.value === "true"})}
                                      className="w-full px-2 py-1 border rounded text-sm">
                                      <option value="false">No (గళకళ - storyteller != writer)</option>
                                      <option value="true">Yes (కలం-గళం - storyteller = writer)</option>
                                    </select>
                                  </div>
                                  <div className="col-span-2">
                                    <label className="text-xs text-gray-500 block">Category</label>
                                    <select value={editFields.category}
                                      onChange={(e) => setEditFields({...editFields, category: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm">
                                      <option value="">— None —</option>
                                      <option value="గళకళ">గళకళ (Voice Art)</option>
                                      <option value="కలం-గళం">కలం-గళం (Writer-Voice)</option>
                                      <option value="తొలి కథనం - గళం">తొలి కథనం - గళం (Debut Voice)</option>
                                      <option value="తొలి కథనం - కలం">తొలి కథనం - కలం (Debut Writer-Voice)</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500 block">Start Time (H:MM:SS)</label>
                                    <input value={editFields.story_start_time}
                                      onChange={(e) => setEditFields({...editFields, story_start_time: e.target.value})}
                                      className="w-full px-2 py-1 border rounded text-sm" placeholder="0:10:04" />
                                    {editFields.story_start_time && (
                                      <span className="text-xs text-gray-400">{timeToSec(editFields.story_start_time)}s</span>
                                    )}
                                  </div>
                                  <div className="col-span-2 pt-1 border-t">
                                    <button onClick={(e) => {
                                        e.stopPropagation();
                                        bulkRenameStoryteller(p.storyteller_name_telugu, editFields.storyteller_name_telugu, editFields.title);
                                      }}
                                      disabled={isSavingPerf || editFields.storyteller_name_telugu === p.storyteller_name_telugu}
                                      className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-30">
                                      Rename ALL "{p.storyteller_name_telugu}" → "{editFields.storyteller_name_telugu}"
                                    </button>
                                    <span className="text-xs text-gray-400 ml-2">Updates all performances with this exact name</span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm space-y-1">
                                  <div><span className="text-gray-500">Telugu:</span> {p.storyteller_name_telugu || "—"} {p.title && <span className="text-purple-600 text-xs">({p.title})</span>}</div>
                                  <div><span className="text-gray-500">English:</span> {p.storyteller_name_english || "—"}</div>
                                  <div><span className="text-gray-500">Author:</span> {p.is_author ? "Yes (కలం)" : "No (గళం)"}</div>
                                  <div><span className="text-gray-500">Category:</span> {p.category || "—"}</div>
                                  <div><span className="text-gray-500">Story:</span> {story?.title_telugu || "—"} | <span className="text-gray-500">Writer:</span> {writer || "—"}</div>
                                  <div><span className="text-gray-500">Start:</span> {p.story_start_time || "—"} | <span className="text-gray-500">End:</span> {p.story_end_time || "—"}</div>
                                  <div><span className="text-gray-500">Video:</span> {ep?.video_id || "—"} | <span className="text-gray-500">Date:</span> {ep?.publish_date || "—"}</div>
                                  <div><span className="text-gray-500">Rating Discussion:</span> {p.rating_discussion ?? "—"}</div>
                                </div>
                              )}
                            </div>
                            {/* Column 2: Analysis */}
                            <div>
                              <div className="text-xs font-semibold text-gray-500 mb-1">Analysis</div>
                              <div className="text-sm text-gray-700 max-h-48 overflow-y-auto whitespace-pre-wrap bg-white rounded p-2 border">
                                {p.analysis_text || "No analysis available."}
                              </div>
                            </div>
                          </div>
                          {videoUrl && (
                            <div className="mt-3">
                              <iframe
                                width="560" height="315"
                                src={`https://www.youtube.com/embed/${ep.video_id}?start=${p.story_start_sec || 0}`}
                                allow="encrypted-media" allowFullScreen title="Performance"
                                className="rounded-lg shadow max-w-full"
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {paged.length === 0 && (
                <tr><td colSpan={18} className="text-center py-8 text-gray-400">No performances found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-4">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
            className="px-3 py-1.5 rounded border text-sm font-semibold disabled:opacity-30 hover:bg-gray-50">← Prev</button>
          <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
            className="px-3 py-1.5 rounded border text-sm font-semibold disabled:opacity-30 hover:bg-gray-50">Next →</button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   Storytellers Management Tab (Add/Edit hosts & analysts)
   ═══════════════════════════════════════════════════════ */
const StorytellersTab = ({ supabase }) => {
  const [people, setPeople] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPerson, setNewPerson] = useState({ storyteller_telugu: "", storyteller_english: "", whatsapp: "", email: "", introduction: "", is_host: false, is_story_analyst: false, is_judge: false, gender: "" });
  const [isSaving, setIsSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" | "hosts" | "analysts" | "judges"
  const [search, setSearch] = useState("");
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState([]); // array of storyteller_ids
  const [merging, setMerging] = useState(false);
  const [editingId, setEditingId] = useState(null); // storyteller_id being edited
  const [editName, setEditName] = useState({ telugu: "", english: "" });

  useEffect(() => { fetchPeople(); }, []);

  const fetchPeople = async () => {
    setIsLoading(true);
    try {
      const { data } = await supabase.from("storytellers").select("storyteller_id, storyteller_telugu, storyteller_english, whatsapp, email, introduction, photo_url, is_host, is_story_analyst, is_judge, slot_id, gender").order("storyteller_english");
      setPeople(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMergeSelect = (id) => {
    setMergeSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMerge = async () => {
    if (mergeSelected.length < 2) { alert("Select at least 2 records to merge."); return; }
    const selectedPeople = people.filter((p) => mergeSelected.includes(p.storyteller_id));
    // Primary = first selected (user picks order)
    const primary = selectedPeople[0];
    const duplicateIds = mergeSelected.slice(1);
    const dupeNames = selectedPeople.slice(1).map((p) => p.storyteller_telugu || p.storyteller_english).join(", ");

    if (!window.confirm(
      `Merge into: ${primary.storyteller_telugu || primary.storyteller_english}\n\nDuplicates to merge & delete:\n${dupeNames}\n\nAll references (performances, slots, slides, judge_scores) will be transferred to the primary record. This cannot be undone.`
    )) return;

    setMerging(true);
    try {
      // Transfer references from duplicates to primary
      for (const dupeId of duplicateIds) {
        // Transfer performances
        await supabase.from("performances").update({ storyteller_id: primary.storyteller_id }).eq("storyteller_id", dupeId);
        // Transfer storyteller_slides
        await supabase.from("storyteller_slides").update({ slot_id: primary.slot_id }).eq("slot_id", people.find(p => p.storyteller_id === dupeId)?.slot_id);
        // Transfer judge scores
        await supabase.from("jeedigunta_judge_scores").update({ judge_storyteller_id: primary.storyteller_id }).eq("judge_storyteller_id", dupeId);
        // Merge fields: fill in blanks on primary from duplicate
        const dupe = selectedPeople.find((p) => p.storyteller_id === dupeId);
        const updates = {};
        if (!primary.photo_url && dupe.photo_url) updates.photo_url = dupe.photo_url;
        if (!primary.whatsapp && dupe.whatsapp) updates.whatsapp = dupe.whatsapp;
        if (!primary.email && dupe.email) updates.email = dupe.email;
        if (!primary.introduction && dupe.introduction) updates.introduction = dupe.introduction;
        if (!primary.gender && dupe.gender) updates.gender = dupe.gender;
        if (!primary.storyteller_telugu && dupe.storyteller_telugu) updates.storyteller_telugu = dupe.storyteller_telugu;
        if (!primary.storyteller_english && dupe.storyteller_english) updates.storyteller_english = dupe.storyteller_english;
        if (dupe.is_host) updates.is_host = true;
        if (dupe.is_story_analyst) updates.is_story_analyst = true;
        if (dupe.is_judge) updates.is_judge = true;
        if (Object.keys(updates).length > 0) {
          await supabase.from("storytellers").update(updates).eq("storyteller_id", primary.storyteller_id);
        }
        // Delete duplicate
        await supabase.from("storytellers").delete().eq("storyteller_id", dupeId);
      }
      alert(`Merged ${duplicateIds.length} duplicate(s) into ${primary.storyteller_telugu || primary.storyteller_english}`);
      setMergeSelected([]);
      setMergeMode(false);
      await fetchPeople();
    } catch (err) {
      alert("Merge failed: " + (err?.message || "Unknown error"));
    } finally {
      setMerging(false);
    }
  };

  const toggleFlag = async (id, field, currentValue) => {
    try {
      await supabase.from("storytellers").update({ [field]: !currentValue }).eq("storyteller_id", id);
      await fetchPeople();
    } catch (err) {
      alert("Failed to update: " + err.message);
    }
  };

  const startEditing = (person) => {
    setEditingId(person.storyteller_id);
    setEditName({ telugu: person.storyteller_telugu || "", english: person.storyteller_english || "" });
  };

  const saveNameEdit = async () => {
    if (!editingId) return;
    try {
      await supabase.from("storytellers").update({
        storyteller_telugu: editName.telugu || null,
        storyteller_english: editName.english || null,
      }).eq("storyteller_id", editingId);
      setEditingId(null);
      await fetchPeople();
    } catch (err) {
      alert("Failed to save: " + err.message);
    }
  };

  const addPerson = async () => {
    if (!newPerson.storyteller_telugu && !newPerson.storyteller_english) {
      alert("Please enter at least one name.");
      return;
    }
    setIsSaving(true);
    try {
      const { error } = await supabase.from("storytellers").insert({
        storyteller_telugu: newPerson.storyteller_telugu || null,
        storyteller_english: newPerson.storyteller_english || null,
        whatsapp: newPerson.whatsapp || null,
        email: newPerson.email || null,
        introduction: newPerson.introduction || null,
        is_host: newPerson.is_host,
        is_story_analyst: newPerson.is_story_analyst,
        is_judge: newPerson.is_judge,
        gender: newPerson.gender || null,
      });
      if (error) throw error;
      setNewPerson({ storyteller_telugu: "", storyteller_english: "", whatsapp: "", email: "", introduction: "", is_host: false, is_story_analyst: false, is_judge: false, gender: "" });
      setShowAddForm(false);
      await fetchPeople();
    } catch (err) {
      alert("Failed to add: " + (err?.message || "Unknown error"));
    } finally {
      setIsSaving(false);
    }
  };

  const filtered = useMemo(() => {
    let list = people;
    if (filter === "hosts") list = list.filter((p) => p.is_host);
    else if (filter === "analysts") list = list.filter((p) => p.is_story_analyst);
    else if (filter === "judges") list = list.filter((p) => p.is_judge);
    else if (filter === "standalone") list = list.filter((p) => !p.slot_id);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        (p.storyteller_telugu || "").toLowerCase().includes(q) ||
        (p.storyteller_english || "").toLowerCase().includes(q) ||
        (p.email || "").toLowerCase().includes(q) ||
        (p.whatsapp || "").includes(q)
      );
    }
    return list;
  }, [people, filter, search]);

  if (isLoading) return <div className="py-10 text-center text-gray-600">Loading storytellers...</div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2">
          {[
            { key: "all", label: `All (${people.length})` },
            { key: "hosts", label: `Hosts (${people.filter((p) => p.is_host).length})` },
            { key: "analysts", label: `Analysts (${people.filter((p) => p.is_story_analyst).length})` },
            { key: "judges", label: `Judges (${people.filter((p) => p.is_judge).length})` },
            { key: "standalone", label: `Standalone (${people.filter((p) => !p.slot_id).length})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                filter === f.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            placeholder="Search name, email, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm w-56 focus:ring-2 focus:ring-blue-500"
          />
          {search && <span className="text-xs text-gray-500">{filtered.length} found</span>}
          <button
            onClick={() => { setMergeMode(!mergeMode); setMergeSelected([]); }}
            className={`px-3 py-2 rounded-md text-sm font-semibold border transition ${
              mergeMode ? "bg-red-600 text-white border-red-600" : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {mergeMode ? "Cancel Merge" : "Merge Duplicates"}
          </button>
          {mergeMode && mergeSelected.length >= 2 && (
            <button
              onClick={handleMerge}
              disabled={merging}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
            >
              {merging ? "Merging..." : `Merge ${mergeSelected.length} Records`}
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700"
          >
            {showAddForm ? "Cancel" : "+ Add"}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-lg shadow p-4 mb-4 border-l-4 border-green-500">
          <h3 className="text-sm font-bold text-gray-700 mb-3">Add New Storyteller</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-0.5">Telugu Name</label>
              <input className="w-full p-2 border rounded text-sm" value={newPerson.storyteller_telugu} onChange={(e) => setNewPerson({ ...newPerson, storyteller_telugu: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-0.5">English Name</label>
              <input className="w-full p-2 border rounded text-sm" value={newPerson.storyteller_english} onChange={(e) => setNewPerson({ ...newPerson, storyteller_english: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-0.5">WhatsApp</label>
              <input className="w-full p-2 border rounded text-sm" value={newPerson.whatsapp} onChange={(e) => setNewPerson({ ...newPerson, whatsapp: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-0.5">Email</label>
              <input className="w-full p-2 border rounded text-sm" value={newPerson.email} onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-gray-500 mb-0.5">Introduction</label>
              <textarea className="w-full p-2 border rounded text-sm h-16 resize-none" value={newPerson.introduction} onChange={(e) => setNewPerson({ ...newPerson, introduction: e.target.value })} />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={newPerson.is_host} onChange={(e) => setNewPerson({ ...newPerson, is_host: e.target.checked })} />
                <span className="font-medium text-green-700">Host</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={newPerson.is_story_analyst} onChange={(e) => setNewPerson({ ...newPerson, is_story_analyst: e.target.checked })} />
                <span className="font-medium text-purple-700">Story Analyst</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={newPerson.is_judge} onChange={(e) => setNewPerson({ ...newPerson, is_judge: e.target.checked })} />
                <span className="font-medium text-amber-700">Judge</span>
              </label>
              <div className="flex items-center gap-1.5">
                <label className="text-xs font-semibold text-gray-500">Gender:</label>
                <select className="p-1.5 border rounded text-sm" value={newPerson.gender} onChange={(e) => setNewPerson({ ...newPerson, gender: e.target.value })}>
                  <option value="">—</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            </div>
            <div className="flex items-end justify-end">
              <button onClick={addPerson} disabled={isSaving} className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                {isSaving ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((person) => (
          <div key={person.storyteller_id} className={`bg-white rounded-lg shadow-sm p-3 flex items-center gap-3 border-l-4 ${mergeSelected.includes(person.storyteller_id) ? "border-red-500 bg-red-50" : "border-gray-200"}`}>
            {mergeMode && (
              <input
                type="checkbox"
                checked={mergeSelected.includes(person.storyteller_id)}
                onChange={() => toggleMergeSelect(person.storyteller_id)}
                className="w-5 h-5 shrink-0 accent-red-600"
              />
            )}
            {person.photo_url ? (
              <img src={person.photo_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 shrink-0">👤</div>
            )}
            <div className="flex-1 min-w-0">
              {editingId === person.storyteller_id ? (
                <div className="flex flex-col gap-1">
                  <div className="flex gap-1 items-center">
                    <input
                      type="text"
                      value={editName.telugu}
                      onChange={(e) => setEditName({ ...editName, telugu: e.target.value })}
                      className="border border-blue-300 rounded px-2 py-0.5 text-sm flex-1 min-w-0"
                      placeholder="Telugu name"
                      autoFocus
                    />
                    <span className="text-gray-400">—</span>
                    <input
                      type="text"
                      value={editName.english}
                      onChange={(e) => setEditName({ ...editName, english: e.target.value })}
                      className="border border-blue-300 rounded px-2 py-0.5 text-sm flex-1 min-w-0"
                      placeholder="English name"
                      onKeyDown={(e) => e.key === "Enter" && saveNameEdit()}
                    />
                    <button onClick={saveNameEdit} className="text-green-600 hover:text-green-800 text-sm font-bold px-1">✓</button>
                    <button onClick={() => setEditingId(null)} className="text-red-500 hover:text-red-700 text-sm font-bold px-1">✕</button>
                  </div>
                </div>
              ) : (
                <div className="font-semibold text-gray-800 text-sm cursor-pointer hover:text-blue-600" onClick={() => startEditing(person)} title="Click to edit name">
                  {person.storyteller_telugu} — {person.storyteller_english}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {person.slot_id ? `Slot ${person.slot_id}` : "No slot (standalone)"}
                {person.email && ` · ${person.email}`}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select
                value={person.gender || ""}
                onChange={async (e) => {
                  try {
                    await supabase.from("storytellers").update({ gender: e.target.value || null }).eq("storyteller_id", person.storyteller_id);
                    await fetchPeople();
                  } catch (err) { alert("Failed: " + err.message); }
                }}
                className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                  person.gender === "Male" ? "bg-blue-50 text-blue-700 border-blue-300" : person.gender === "Female" ? "bg-pink-50 text-pink-700 border-pink-300" : "bg-white text-gray-400 border-gray-200"
                }`}
              >
                <option value="">Gender</option>
                <option value="Male">♂ Male</option>
                <option value="Female">♀ Female</option>
              </select>
              <button
                onClick={() => toggleFlag(person.storyteller_id, "is_host", person.is_host)}
                className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                  person.is_host ? "bg-green-100 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:border-green-300"
                }`}
              >
                🎤 Host
              </button>
              <button
                onClick={() => toggleFlag(person.storyteller_id, "is_story_analyst", person.is_story_analyst)}
                className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                  person.is_story_analyst ? "bg-purple-100 text-purple-800 border-purple-300" : "bg-white text-gray-400 border-gray-200 hover:border-purple-300"
                }`}
              >
                📝 Analyst
              </button>
              <button
                onClick={() => toggleFlag(person.storyteller_id, "is_judge", person.is_judge)}
                className={`px-2 py-1 rounded text-xs font-semibold border transition ${
                  person.is_judge ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white text-gray-400 border-gray-200 hover:border-amber-300"
                }`}
              >
                ⚖️ Judge
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Manage;
